import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, Loader2, AlertCircle, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import { fetchPerformanceData } from './dataService';
import { PerformanceInsights } from './types';
import LoadingState from './LoadingState';
import EmptyState from './EmptyState';
import AnalysisTabs from './AnalysisTabs';
const AIPerformanceAdvisorTab = () => {
  const [insights, setInsights] = useState<PerformanceInsights | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisStartTime, setAnalysisStartTime] = useState<number | null>(null);
  const handleGenerateInsights = async () => {
    setIsAnalyzing(true);
    setIsEnhancing(false);
    setError(null);
    setAnalysisStartTime(Date.now());
    console.log('🚀 AI PERFORMANCE ADVISOR: Starting optimized analysis...');
    try {
      // Phase 1: Fetch performance data
      console.log('📊 Fetching performance data...');
      const performanceData = await fetchPerformanceData();
      console.log('📈 Performance data summary:', {
        goals: performanceData.performanceGoals.length,
        appraisals: performanceData.performanceAppraisals.length,
        bands: performanceData.performanceBands.length,
        disagreements: performanceData.bandDisagreements.length,
        users: performanceData.users.length
      });

      // Phase 2: Call optimized AI analysis
      console.log('🤖 Calling optimized AI analysis function...');
      setIsEnhancing(true); // Show AI enhancement state

      const {
        data,
        error
      } = await supabase.functions.invoke('analyze-performance-data', {
        body: {
          performanceData,
          analysisType: 'optimized'
        }
      });
      if (error) {
        console.error('❌ AI Analysis Error:', error);
        throw error;
      }
      const analysisTime = analysisStartTime ? Date.now() - analysisStartTime : 0;
      console.log(`✅ AI Analysis Complete in ${analysisTime}ms:`, data);
      setInsights(data);
      toast.success(`Performance insights generated successfully! (${(analysisTime / 1000).toFixed(1)}s)`);
    } catch (error) {
      console.error('❌ Error generating insights:', error);
      setError(error.message || 'Failed to generate insights');
      const analysisTime = analysisStartTime ? Date.now() - analysisStartTime : 0;
      toast.error(`Failed to generate insights after ${(analysisTime / 1000).toFixed(1)}s. Please try again.`);
    } finally {
      setIsAnalyzing(false);
      setIsEnhancing(false);
      setAnalysisStartTime(null);
    }
  };
  const getLoadingMessage = () => {
    if (!isAnalyzing) return '';
    const elapsed = analysisStartTime ? Date.now() - analysisStartTime : 0;
    const seconds = Math.floor(elapsed / 1000);
    if (isEnhancing) {
      return `Enhancing with AI insights...`;
    }
    return `Processing performance data... (${seconds}s)`;
  };
  return <div className="h-full flex flex-col overflow-hidden space-y-0">
      {/* Modern Header Section */}
      <div className="bg-gradient-to-r from-primary/10 to-blue-600/10 rounded-xl border border-primary/20 p-2 mx-0 px-[20px] my-[10px] py-[9px]">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Brain className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  AI Performance Advisor
                </h2>
                <p className="text-gray-300 text-sm">
                  Generate intelligent insights from your performance management data
                </p>
                <p className="text-[12px] text-white">(Note: AI hallucination may occur. Validation of generated information is essential.)</p>
              </div>
            </div>
          </div>
          
          <Button onClick={handleGenerateInsights} disabled={isAnalyzing} className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 text-white font-semibold px-6 py-3 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105">
            {isAnalyzing ? <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isEnhancing ? 'Enhancing...' : 'Analyzing...'}
              </> : <>
                Generate Insights
              </>}
          </Button>
        </div>
        
        {/* Progress indicator */}
        {isAnalyzing && <div className="mt-3 flex items-center gap-2 text-sm text-gray-300">
            <Clock className="h-4 w-4" />
            <span>{getLoadingMessage()}</span>
          </div>}
      </div>

      {/* Content */}
      <div className="flex-grow overflow-auto space-y-1">
        {isAnalyzing && <LoadingState />}
        
        {error && <Card className="bg-gradient-to-r from-red-900/20 to-red-800/20 border-red-500/50">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Analysis Failed</h3>
              <p className="text-gray-300">{error}</p>
              <Button onClick={handleGenerateInsights} className="mt-4" variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>}
        
        {!isAnalyzing && !error && !insights && <EmptyState />}
        
        {insights && !isAnalyzing && <div className="space-y-2">
            {/* Show AI recommendations if available */}
            {insights.executiveSummary?.aiRecommendations && <Card className="bg-gradient-to-r from-primary/10 to-blue-600/10 rounded-xl border border-primary/20 p-2 py-0 my-[5px]">
                <CardHeader className="pb-2 py-0 my-[3px]">
                  <CardTitle className="text-white flex items-center gap-2 text-[15px] font-bold py-0 my-0">
                    AI-Enhanced Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-[13px] my-[7px] py-px">
                  <ul className="space-y-1">
                    {insights.executiveSummary.aiRecommendations.map((rec: string, index: number) => <li key={index} className="text-gray-300 flex items-start gap-2">
                        <span className="text-white text-[13px]">•</span>
                        {rec}
                      </li>)}
                  </ul>
                </CardContent>
              </Card>}
            
            <AnalysisTabs insights={insights} />
          </div>}
      </div>
    </div>;
};
export default AIPerformanceAdvisorTab;